import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import sanitizeHtml from "../lib/sanitizeHtml";
import { Avatar, Username, VipBadge } from "../components/UserBadge";
import { useAuth } from "../context/AuthContext";
import { useT, useLang } from "../i18n";
import { REACTIONS } from "../constants/loadout";
import { Lock, Pin, Share2, Bookmark, Hash, Flag, Archive, AlertTriangle, Languages, X, Trash2, Move, Tag, Shield, ChevronDown, Eye, EyeOff, Zap, Activity, User, MessageSquare, Clock, Users, ShieldCheck, Ban, CheckCircle2, Star, Quote, Pencil, Flame, Check } from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import ReplyEditor, { renderMarkdown, AttachmentList } from "../components/ReplyEditor";
import "./Topic.css";

const PROFILE_BANNERS = {
  tactical: "https://images.unsplash.com/photo-1542751371-adc38448a05e",
  neon_grid: "https://images.unsplash.com/photo-1538481199705-c710c4e965fc",
  carbon: "https://images.unsplash.com/photo-1551103782-8ab07afd45c1",
  blood: "https://images.unsplash.com/photo-1614680376573-df3480f0c6ff",
  ice: "https://images.unsplash.com/photo-1547036967-23d11aacaee0",
  default: "/banners/default_profile_banner.png",
};

export default function Topic() {
  const { topicId } = useParams();
  const { user } = useAuth();
  const [lightbox, setLightbox] = useState(null);
  
  const handleContentClick = (e) => {
    if (e.target.tagName === 'IMG' && !e.target.closest('a')) {
      setLightbox(e.target.src);
    }
  };
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [reportingPost, setReportingPost] = useState(null);
  const [reportReason, setReportReason] = useState("");
  
  // Edit State
  const [editingTopic, setEditingTopic] = useState(false);
  const [editTopicTitle, setEditTopicTitle] = useState("");
  const [editTopicDesc, setEditTopicDesc] = useState("");
  
  const [editingPostId, setEditingPostId] = useState(null);
  const [editPostContent, setEditPostContent] = useState("");
  
  // Translation Cache State
  const [translations, setTranslations] = useState({});
  
  // Moderator Warnings State
  const [warningPost, setWarningPost] = useState(null);
  const [warningReason, setWarningReason] = useState("");
  const [warningPoints, setWarningPoints] = useState(1);

  // Moderator Saved Actions State
  const [savedActions, setSavedActions] = useState([]);
  const [showModMenu, setShowModMenu] = useState(false);
  const [topicTags, setTopicTags] = useState([]);
  const [editingTags, setEditingTags] = useState(false);
  const [tagInput, setTagInput] = useState("");

  const isMod = user && ["mod", "admin", "root"].includes(user?.role);
  const t = useT();
  const lang = useLang();
  const queryClient = useQueryClient();

  const { data: onlinePresence } = useQuery({
    queryKey: ["onlinePresence"],
    queryFn: async () => (await api.get("/presence/online")).data,
    refetchInterval: 30000
  });

  const viewingMembers = (onlinePresence?.users || []).filter(p => p.current_path && p.current_path.includes("/topic/" + topicId));
  const [topicRating, setTopicRating] = useState(0);

  const handleTranslate = async (id, text, isTitle = false) => {
    if (translations[id]) {
      setTranslations(prev => ({
        ...prev,
        [id]: { ...prev[id], showTranslated: !prev[id].showTranslated }
      }));
      return;
    }

    setTranslations(prev => ({
      ...prev,
      [id]: { loading: true, originalText: text, showTranslated: false }
    }));

    try {
      const { data: res } = await api.post("/translate", { text, target_lang: lang });
      setTranslations(prev => ({
        ...prev,
        [id]: {
          loading: false,
          originalText: text,
          translatedText: res.translated_text,
          showTranslated: true
        }
      }));
    } catch (e) {
      toast.error("Translation failed");
      setTranslations(prev => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
    }
  };

  const load = () => api.get(`/topics/${topicId}`).then((r) => {
    setData(r.data);
    setTopicTags(r.data.topic?.tags || []);
  });
  
  useEffect(() => { 
    load(); 
  }, [topicId]);

  useEffect(() => {
    if (isMod) {
      api.get("/idg/admin/saved-actions").then((r) => setSavedActions(r.data || [])).catch(() => {});
    }
  }, [isMod]);

  if (!data) return <div className="text-zinc-500 font-mono text-xs p-12 text-center">{t('common.loading')}</div>;

  const handleReply = async () => {
    if (!user) return toast.error(t('topic.login_to_reply'));
    if (!reply.trim()) return;
    try {
      await api.post("/posts", { topic_id: topicId, content: reply, attachments: replyAttachments });
      setReply(""); setReplyAttachments([]);
      load();
      queryClient.invalidateQueries({ queryKey: ["boards"] });
      if (data && data.topic) {
        queryClient.invalidateQueries({ queryKey: ["board", data.topic.board_id] });
      }
      toast.success(t('topic.reply_posted'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const handleReact = async (postId, type) => {
    if (!user) return toast.error(t('topic.login_to_react'));
    await api.post(`/posts/${postId}/react`, { type });
    load();
  };

  const submitReport = async () => {
    try {
      await api.post("/reports", { target_type: "post", target_id: reportingPost, reason: reportReason });
      toast.success(t('topic.report_submitted'));
      setReportingPost(null); setReportReason("");
    } catch (e) {
      toast.error(t('topic.report_failed'));
    }
  };

  const submitWarning = async () => {
    if (!warningPost) return;
    try {
      await api.post("/idg/moderation/warn", {
        user_id: warningPost.author_id,
        reason: warningReason,
        points: parseInt(warningPoints)
      });
      toast.success(t('topic.member_warned'));
      setWarningPost(null);
      setWarningReason("");
      setWarningPoints(1);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const handleApplySavedAction = async (actionId) => {
    if (!actionId) return;
    try {
      await api.post(`/idg/moderation/topics/${topicId}/apply-action/${actionId}`);
      toast.success(t('common.success'));
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const togglePin = async () => {
    try {
      await api.patch(`/admin/topics/${topicId}`, { pinned: !data.topic.pinned });
      toast.success(data.topic.pinned ? t('topic.topic_unpinned') : t('topic.topic_pinned'));
      load();
    } catch(e) { toast.error("Failed to pin topic"); }
  };

  const toggleLock = async () => {
    try {
      await api.patch(`/admin/topics/${topicId}`, { locked: !data.topic.locked });
      toast.success(data.topic.locked ? t('topic.topic_unlocked') : t('topic.topic_locked'));
      load();
    } catch(e) { toast.error("Failed to lock topic"); }
  };

  const toggleArchive = async () => {
    try {
      await api.patch(`/admin/topics/${topicId}`, { archived: !data.topic.archived });
      toast.success(data.topic.archived ? "Topic dezarhivat" : "Topic arhivat");
      load();
    } catch(e) { toast.error("Eroare la arhivare"); }
  };

  const handleDeleteTopic = async () => {
    if (!window.confirm("Sigur ștergi acest topic? Acțiunea este ireversibilă!")) return;
    try {
      await api.delete(`/topics/${topicId}`);
      toast.success("Topic șters");
      window.history.back();
    } catch(e) { toast.error("Eroare la ștergere"); }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm("Ștergi această postare?")) return;
    try {
      await api.delete(`/posts/${postId}`);
      toast.success("Postare ștearsă");
      load();
    } catch(e) { toast.error("Eroare la ștergere"); }
  };

  const handleAddTag = async () => {
    const tag = tagInput.trim().toLowerCase().replace(/\s+/g, '-');
    if (!tag || topicTags.includes(tag)) return;
    const newTags = [...topicTags, tag];
    try {
      await api.patch(`/topics/${topicId}`, { tags: newTags });
      setTopicTags(newTags);
      setTagInput("");
      toast.success("Tag adăugat");
    } catch(e) { toast.error("Eroare la adăugare tag"); }
  };

  const handleRemoveTag = async (tag) => {
    const newTags = topicTags.filter(t => t !== tag);
    try {
      await api.patch(`/topics/${topicId}`, { tags: newTags });
      setTopicTags(newTags);
    } catch(e) { toast.error("Eroare"); }
  };

  const handleEditTopic = async () => {
    if (!editTopicTitle.trim()) return toast.error(t('topic.title_required'));
    try {
      await api.patch(`/topics/${topicId}`, { title: editTopicTitle, description: editTopicDesc });
      toast.success(t('topic.topic_updated'));
      setEditingTopic(false);
      load();
    } catch(e) { toast.error(t('topic.failed_update_topic')); }
  };

  const handleEditPost = async (postId) => {
    if (!editPostContent.trim()) return toast.error(t('topic.content_required'));
    try {
      await api.patch(`/posts/${postId}`, { content: editPostContent });
      toast.success(t('topic.post_updated'));
      setEditingPostId(null);
      load();
    } catch(e) { toast.error(t('topic.failed_update_post')); }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success(t('common.link_copied'));
  };

  const handleSave = () => {
    toast.success(t('topic.topic_saved'));
  };

  return (
    <div className="space-y-6">
      
      {/* 1. TOPIC HERO / HEADER BANNER (Obsidian Tactical) */}
      <section className="bg-[#090c12] border border-[#151924] relative overflow-hidden shadow-2xl p-5 sm:p-6" data-testid="topic-hero">
        {/* Top ambient highlight line */}
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[var(--tf-primary)] to-transparent opacity-80" />

        <div className="relative z-10 space-y-3">
          {/* Breadcrumb Navigation */}
          <nav className="flex items-center gap-2 text-xs font-mono text-zinc-500 uppercase tracking-widest flex-wrap">
            <Link to="/forums" className="hover:text-white transition-colors">{t('nav.forums', 'FORUM')}</Link>
            <span className="text-zinc-700">/</span>
            <Link to={`/forums/${data.topic.board_id}`} className="text-[var(--tf-primary)] hover:text-white transition-colors">
              {data.topic.board_name}
            </Link>
          </nav>

          {/* Badges Row */}
          <div className="flex items-center gap-2 flex-wrap">
            {data.topic.pinned && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-chakra font-black tracking-wider uppercase bg-amber-500/10 text-amber-400 border border-amber-500/30">
                <Flame className="w-3 h-3 text-amber-400" /> {t('common.hot_topic', 'HOT TOPIC')}
              </span>
            )}
            {!data.topic.locked && !data.topic.archived && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-chakra font-black tracking-wider uppercase bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                <Check className="w-3 h-3 text-emerald-400" /> {t('common.active', 'ACTIV')}
              </span>
            )}
            {!data.topic.locked && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-chakra font-black tracking-wider uppercase bg-cyan-500/10 text-cyan-400 border border-cyan-500/30">
                <Eye className="w-3 h-3 text-cyan-400" /> {t('common.public', 'PUBLIC')}
              </span>
            )}
            {data.topic.locked && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-chakra font-black tracking-wider uppercase bg-rose-500/10 text-rose-400 border border-rose-500/30">
                <Lock className="w-3 h-3 text-rose-400" /> {t('common.locked', 'BLOCAT')}
              </span>
            )}
          </div>

          {/* Topic Title & Edit State */}
          {editingTopic ? (
            <div className="bg-[#06080d] p-4 border border-[#141824] space-y-3 mt-3">
              <input 
                type="text" 
                value={editTopicTitle} 
                onChange={(e) => setEditTopicTitle(e.target.value)} 
                className="w-full bg-[#090c12] border border-[#181e2b] p-3 text-lg font-chakra font-bold text-white outline-none focus:border-[var(--tf-primary)]" 
                placeholder="Titlu topic..." 
              />
              <input 
                type="text" 
                value={editTopicDesc} 
                onChange={(e) => setEditTopicDesc(e.target.value)} 
                className="w-full bg-[#090c12] border border-[#181e2b] p-3 text-xs text-white outline-none focus:border-[var(--tf-primary)]" 
                placeholder="Descriere topic..." 
              />
              <div className="flex gap-2">
                <button onClick={handleEditTopic} className="bg-[var(--tf-primary)] text-black px-4 py-2 font-chakra font-black text-xs uppercase">{t('common.save')}</button>
                <button onClick={() => setEditingTopic(false)} className="bg-[#090c12] border border-[#181e2b] text-zinc-400 px-4 py-2 font-chakra font-bold text-xs uppercase">{t('common.cancel')}</button>
              </div>
            </div>
          ) : (
            <div className="space-y-1 pt-1">
              <div className="flex items-center justify-between gap-4">
                <h1 className="font-chakra font-black text-2xl sm:text-3xl lg:text-4xl uppercase tracking-wider text-white flex-1">
                  {data.topic.badge && <span className="mr-2.5 text-[14px] align-middle" dangerouslySetInnerHTML={{ __html: sanitizeHtml(data.topic.badge) }} />}
                  {translations[`topic_${data.topic.id}_title`]?.showTranslated 
                    ? translations[`topic_${data.topic.id}_title`].translatedText 
                    : data.topic.title}
                </h1>
                
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => handleTranslate(`topic_${data.topic.id}_title`, data.topic.title, true)}
                    className="p-2 bg-[#06080d] border border-[#141824] hover:border-[var(--tf-primary)] text-zinc-400 hover:text-[var(--tf-primary)] transition-colors"
                    title={translations[`topic_${data.topic.id}_title`]?.showTranslated ? t('topic.show_original') : t('topic.translate_post')}
                  >
                    <Languages className={`w-4 h-4 ${translations[`topic_${data.topic.id}_title`]?.loading ? 'animate-pulse text-[var(--tf-primary)]' : ''}`} />
                  </button>
                  {(isMod || user?.id === data.posts[0]?.author_id) && (
                    <button 
                      onClick={() => { setEditTopicTitle(data.topic.title); setEditTopicDesc(data.topic.description || ""); setEditingTopic(true); }} 
                      className="p-2 bg-[#06080d] border border-[#141824] hover:border-[var(--tf-primary)] text-zinc-400 hover:text-[var(--tf-primary)] transition-colors"
                      title="Editează titlu"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              <p className="text-xs sm:text-sm text-zinc-400 font-mono">
                {translations[`topic_${data.topic.id}_desc`]?.showTranslated 
                  ? translations[`topic_${data.topic.id}_desc`].translatedText 
                  : (data.topic.description || t('topic.discussion', 'Discuție în cadrul comunității.'))}
              </p>
            </div>
          )}

          {/* Telemetry Strip (Viewers, Rating, Tags) */}
          <div className="flex items-center justify-between border-t border-[#141824] pt-4 mt-4 flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <span className="text-[10px] font-chakra font-black text-zinc-500 uppercase tracking-widest">
                VIZUALIZEAZĂ:
              </span>
              <div className="flex -space-x-1.5">
                {viewingMembers.length > 0 ? viewingMembers.map(v => (
                  <div key={v.user_id} className="w-6 h-6 bg-[#06080d] border border-[#181e2b] flex items-center justify-center text-[9px] font-bold text-white relative group cursor-help" title={v.username}>
                    {v.username.charAt(0).toUpperCase()}
                  </div>
                )) : (
                  <span className="text-xs font-mono text-zinc-500">Doar tu</span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-[10px] font-chakra font-black text-zinc-500 uppercase tracking-widest">RATING:</span>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map(star => (
                  <button 
                    key={star}
                    onClick={() => { setTopicRating(star); toast.success("Vot înregistrat!"); }}
                    className={`transition-colors cursor-pointer ${star <= topicRating ? 'text-amber-400' : 'text-zinc-700 hover:text-amber-400/50'}`}
                  >
                    <Star className="w-3.5 h-3.5 fill-current" />
                  </button>
                ))}
              </div>
            </div>

            {/* Tags Strip */}
            <div className="flex flex-wrap items-center gap-2">
              {topicTags.map(tag => (
                <span key={tag} className="flex items-center gap-1 px-2 py-0.5 bg-[var(--tf-primary)]/10 border border-[var(--tf-primary)]/30 text-[var(--tf-primary)] text-[10px] font-mono font-bold uppercase tracking-wider">
                  <Hash className="w-2.5 h-2.5" />
                  <span>{tag}</span>
                  {isMod && (
                    <button onClick={() => handleRemoveTag(tag)} className="ml-0.5 hover:text-red-400 transition-colors">
                      <X className="w-2.5 h-2.5" />
                    </button>
                  )}
                </span>
              ))}
              {isMod && !editingTags && (
                <button onClick={() => setEditingTags(true)} className="flex items-center gap-1 px-2 py-0.5 border border-dashed border-[#181e2b] text-zinc-500 hover:text-[var(--tf-primary)] hover:border-[var(--tf-primary)]/40 text-[10px] font-mono transition-all">
                  <Tag className="w-2.5 h-2.5" /> + Tag
                </button>
              )}
              {isMod && editingTags && (
                <div className="flex items-center gap-1">
                  <input
                    value={tagInput}
                    onChange={e => setTagInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleAddTag(); if (e.key === 'Escape') setEditingTags(false); }}
                    placeholder="tag-nou..."
                    className="bg-[#06080d] border border-[#181e2b] py-0.5 px-2 text-[10px] text-white w-28 outline-none"
                    autoFocus
                  />
                  <button onClick={handleAddTag} className="text-[var(--tf-primary)] p-0.5"><Zap className="w-3 h-3" /></button>
                  <button onClick={() => setEditingTags(false)} className="text-zinc-600 hover:text-white p-0.5"><X className="w-3 h-3" /></button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* 2. TOPIC LAYOUT (Main Posts Stream + Sidebar) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        
        {/* MAIN COLUMN (8 or 9 Cols) */}
        <div className="lg:col-span-9 space-y-5">
          
          {/* Action Toolbar */}
          <div className="bg-[#06080d] border border-[#141824] px-4 py-3 flex items-center justify-between gap-3 flex-wrap shadow-sm">
            <div className="flex items-center gap-2">
              <Link 
                to={`/forums/${data.topic.board_id}`} 
                className="bg-[#090c12] hover:bg-[#0c1018] border border-[#141824] hover:border-[var(--tf-primary)]/50 text-xs font-chakra font-bold uppercase tracking-wider text-zinc-300 hover:text-white px-3 py-1.5 flex items-center gap-1.5 transition-colors"
              >
                <span>&larr;</span> <span>{t('common.back', 'Înapoi')}</span>
              </Link>
              <button 
                onClick={handleSave}
                className="bg-[#090c12] hover:bg-[#0c1018] border border-[#141824] hover:border-[var(--tf-primary)]/50 text-xs font-chakra font-bold uppercase tracking-wider text-zinc-300 hover:text-white px-3 py-1.5 flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                <Bookmark className="w-3.5 h-3.5 text-[var(--tf-primary)]" />
                <span>{t('topic.save_topic', 'Salvează topic')}</span>
              </button>
            </div>

            <div className="flex items-center gap-2">
              <button 
                onClick={handleShare}
                className="bg-[#090c12] hover:bg-[#0c1018] border border-[#141824] hover:border-[var(--tf-primary)]/50 text-xs font-chakra font-bold uppercase tracking-wider text-zinc-300 hover:text-white px-3 py-1.5 flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                <Share2 className="w-3.5 h-3.5 text-cyan-400" />
                <span>{t('common.share', 'Distribuie')}</span>
              </button>

              {/* Moderation Dropdown */}
              {isMod && (
                <div className="relative">
                  <button
                    onClick={() => setShowModMenu(!showModMenu)}
                    className="bg-[var(--tf-primary)] hover:brightness-110 text-black px-3 py-1.5 text-xs font-chakra font-black uppercase tracking-wider flex items-center gap-1.5 transition-all shadow-sm cursor-pointer"
                  >
                    <Shield className="w-3.5 h-3.5" />
                    <span>Moderare</span>
                    <ChevronDown className="w-3 h-3" />
                  </button>

                  {showModMenu && (
                    <div className="absolute right-0 top-full mt-1.5 z-50 bg-[#090c12] border border-[#181e2b] min-w-[220px] shadow-2xl divide-y divide-[#141824] text-xs font-mono">
                      <div className="p-2">
                        <div className="text-[9px] text-zinc-500 uppercase tracking-widest px-2 py-1 font-bold">Acțiuni Topic</div>
                        <button onClick={() => { togglePin(); setShowModMenu(false); }} className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-[#0e131d] text-left text-zinc-300">
                          <Pin className={`w-3.5 h-3.5 ${data.topic.pinned ? 'text-[var(--tf-primary)]' : 'text-zinc-500'}`} />
                          <span>{data.topic.pinned ? 'Dezafișează' : 'Afișează (Pin)'}</span>
                        </button>
                        <button onClick={() => { toggleLock(); setShowModMenu(false); }} className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-[#0e131d] text-left text-zinc-300">
                          <Lock className={`w-3.5 h-3.5 ${data.topic.locked ? 'text-amber-400' : 'text-zinc-500'}`} />
                          <span>{data.topic.locked ? 'Deblochează' : 'Blochează'}</span>
                        </button>
                        <button onClick={() => { toggleArchive(); setShowModMenu(false); }} className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-[#0e131d] text-left text-zinc-300">
                          <Archive className={`w-3.5 h-3.5 ${data.topic.archived ? 'text-blue-400' : 'text-zinc-500'}`} />
                          <span>{data.topic.archived ? 'Dezarhivează' : 'Arhivează'}</span>
                        </button>
                      </div>

                      {savedActions.length > 0 && (
                        <div className="p-2">
                          <div className="text-[9px] text-zinc-500 uppercase tracking-widest px-2 py-1 font-bold">Acțiuni Rapide</div>
                          {savedActions.map(act => (
                            <button key={act.id} onClick={() => { handleApplySavedAction(act.id); setShowModMenu(false); }} className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-[var(--tf-primary)]/10 text-[var(--tf-primary)] text-left">
                              <Zap className="w-3.5 h-3.5" />
                              <span>{act.name}</span>
                            </button>
                          ))}
                        </div>
                      )}

                      <div className="p-2">
                        <button onClick={() => { handleDeleteTopic(); setShowModMenu(false); }} className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-rose-500/10 text-rose-500 text-left font-bold">
                          <Trash2 className="w-3.5 h-3.5" />
                          <span>Șterge topic</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Posts Stream */}
          <div className="space-y-4">
            {data.posts.map((p, idx) => {
              const author = p.author || { username: p.author_username, loadout: {} };
              const isFirst = idx === 0;
              const groupColor = author.group_color || "var(--tf-primary)";

              return (
                <article 
                  key={p.id} 
                  id={`post-${p.id}`}
                  className="bg-[#090c12] border border-[#151924] shadow-2xl relative overflow-hidden flex flex-col md:flex-row transition-all duration-200"
                >
                  {/* Left group color accent stripe */}
                  <div 
                    className="absolute left-0 top-0 bottom-0 w-1" 
                    style={{ backgroundColor: groupColor }} 
                  />

                  {/* LEFT AUTHOR COLUMN WITH COVER BANNER & MICRO-ANIMATIONS */}
                  <aside className="bg-[#06080d] border-b md:border-b-0 md:border-r border-[#141824] w-full md:w-[230px] shrink-0 flex flex-col items-center text-center relative overflow-hidden group/author">
                    
                    {/* User Cover Banner Header */}
                    <div className="w-full h-24 sm:h-28 relative overflow-hidden bg-black">
                      <img 
                        src={author?.banner_url || PROFILE_BANNERS[author?.loadout?.card_background] || author?.cover_url || PROFILE_BANNERS.default} 
                        alt="Banner" 
                        className="w-full h-full object-cover opacity-75 group-hover/author:scale-110 group-hover/author:opacity-95 transition-all duration-500 filter brightness-90" 
                      />
                      {/* Gradient overlay for smooth transition into #06080d */}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#06080d] via-[#06080d]/50 to-transparent" />
                      {/* Top ambient highlight line */}
                      <div className="absolute top-0 inset-x-0 h-0.5 bg-gradient-to-r from-transparent via-[var(--tf-primary)] to-transparent opacity-80" />
                    </div>

                    {/* Author Content Body (Overlapping Avatar) */}
                    <div className="px-5 pb-5 pt-0 -mt-10 relative z-10 w-full flex flex-col items-center space-y-3">
                      
                      {/* Centered Avatar with Glow Frame and Online Dot */}
                      <div className="relative inline-block group/avatar">
                        <div 
                          className="p-1 rounded-none bg-[#06080d] border border-[#141824] group-hover/avatar:border-[var(--tf-primary)] transition-all duration-300 shadow-2xl group-hover/avatar:scale-105"
                          style={{
                            boxShadow: `0 0 20px ${groupColor}25`
                          }}
                        >
                          <Avatar user={author} size={84} hover />
                        </div>
                        {(() => {
                          const isOnline = (onlinePresence?.users || []).some(u => u.user_id === author.id || u.username === author.username);
                          return (
                            <span
                              className={`absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full border-2 border-[#06080d] ${
                                isOnline ? 'bg-emerald-400 shadow-[0_0_8px_#34d399] animate-pulse' : 'bg-rose-500 shadow-[0_0_8px_#ef4444]'
                              }`}
                              title={isOnline ? 'Online' : 'Offline'}
                            />
                          );
                        })()}
                      </div>

                      {/* Username & Role */}
                      <div className="space-y-0.5">
                        <Username user={author} className="text-base font-chakra font-black tracking-wide block transition-transform group-hover/author:scale-105 duration-200" />
                        <div className="text-[11px] font-mono font-bold flex items-center justify-center gap-1 transition-colors" style={{ color: groupColor }}>
                          <Star className="w-3 h-3 fill-current animate-pulse" />
                          <span>{author.role === 'root' ? 'Root' : author.group_name || (author.role === 'admin' ? 'Administrator' : author.role === 'mod' ? 'Moderator' : 'Membru')}</span>
                        </div>
                      </div>

                      {/* Horizontal Stats Mini Grid */}
                      <div className="grid grid-cols-3 divide-x divide-[#141824] bg-[#090c12] border border-[#141824] p-1.5 w-full text-xs font-mono shadow-inner">
                        <div className="text-center group/stat" title="Postări">
                          <div className="text-zinc-500 text-[9px] uppercase font-chakra">Postări</div>
                          <strong className="text-white text-xs group-hover/stat:text-[var(--tf-primary)] transition-colors">{author.message_count || 0}</strong>
                        </div>
                        <div className="text-center group/stat" title="Reacții">
                          <div className="text-emerald-400 text-[9px] uppercase font-chakra">Reacții</div>
                          <strong className="text-emerald-400 text-xs group-hover/stat:text-emerald-300 transition-colors">{author.reaction_score || author.reputation || 0}</strong>
                        </div>
                        <div className="text-center group/stat" title="Puncte">
                          <div className="text-amber-400 text-[9px] uppercase font-chakra">Puncte</div>
                          <strong className="text-amber-400 text-xs group-hover/stat:text-amber-300 transition-colors">{author.points || 0}</strong>
                        </div>
                      </div>

                      {/* Custom Info Fields */}
                      <div className="space-y-1 w-full text-[11px] font-mono pt-1 text-left">
                        <div className="flex items-center justify-between border-b border-[#141824] pb-1">
                          <span className="text-zinc-500">Nume</span>
                          <span className="font-bold text-zinc-300">{author.real_name || author.first_name || author.username}</span>
                        </div>
                        <div className="flex items-center justify-between border-b border-[#141824] pb-1">
                          <span className="text-zinc-500">Localizare</span>
                          <span className="font-bold text-zinc-300">{author.location || "INDUNGI"}</span>
                        </div>
                        <div className="flex items-center justify-between border-b border-[#141824] pb-1">
                          <span className="text-zinc-500">Server</span>
                          <span className="font-bold text-zinc-300 truncate max-w-[95px]" title={author.favorite_server || "[CS 1.6] NUMAI DD2"}>
                            {author.favorite_server || "[CS 1.6] NUMAI DD2"}
                          </span>
                        </div>
                        <div className="flex items-center justify-between border-b border-[#141824] pb-1">
                          <span className="text-zinc-500">Discord</span>
                          <span className="font-bold text-zinc-300 truncate max-w-[95px]">
                            {author.discord_username || author.username?.toLowerCase() || "admin"}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-zinc-500">Steam</span>
                          <Link to={`/u/${author.username}`} className="font-bold text-cyan-400 hover:text-cyan-300 transition-colors">
                            Profil
                          </Link>
                        </div>
                      </div>

                      {/* Member Groups */}
                      {author.groups && author.groups.length > 0 && (
                        <div className="w-full pt-1 space-y-1.5">
                          <div className="text-[9px] font-chakra font-black uppercase text-zinc-500 tracking-wider">
                            Alte Grupuri
                          </div>
                          <div className="flex flex-wrap justify-center gap-1.5">
                            {author.groups.map((g) => (
                              <Link
                                key={g.id}
                                to={`/members?group=${g.id}`}
                                className="px-2 py-0.5 text-[10px] font-chakra font-bold border transition-all hover:scale-105 shadow-sm"
                                style={{
                                  color: g.color || groupColor,
                                  borderColor: `${g.color || groupColor}50`,
                                  backgroundColor: "#080b12",
                                  boxShadow: `inset 0 0 8px ${g.color || groupColor}15`
                                }}
                                title={g.description}
                                translate="no"
                              >
                                {g.name}
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Send Message Button */}
                      <div className="w-full pt-2 mt-auto">
                        <Link
                          to={`/messages?new=${author.username}`}
                          className="w-full py-2 bg-[#090c12] hover:bg-[#0e131d] border border-[#141824] hover:border-[var(--tf-primary)] text-zinc-300 hover:text-white font-chakra font-bold text-xs uppercase flex items-center justify-center gap-2 transition-all shadow-md group/msg"
                        >
                          <MessageSquare className="w-3.5 h-3.5 text-[var(--tf-primary)] group-hover/msg:scale-110 transition-transform" />
                          <span>Mesaj</span>
                        </Link>
                      </div>

                    </div>
                  </aside>

                  {/* RIGHT POST CONTENT AREA */}
                  <section className="flex-1 flex flex-col min-w-0 bg-[#090c12]">
                    {/* Post Top Header */}
                    <div className="px-5 py-3 bg-[#06080d] border-b border-[#141824] flex items-center justify-between flex-wrap gap-2 text-xs font-mono">
                      <div className="flex items-center gap-2 text-zinc-400">
                        <span>{format(new Date(p.created_at), "MMM d, yyyy 'la' HH:mm")}</span>
                        <span>·</span>
                        <span className="text-zinc-300 font-bold uppercase font-chakra">
                          {isFirst ? "PRIMUL MESAJ" : "RĂSPUNS"}
                        </span>
                      </div>

                      <div className="flex items-center gap-3">
                        <strong className="text-amber-400 font-chakra font-black">#{idx + 1}</strong>
                        <button onClick={handleShare} className="text-zinc-500 hover:text-white transition-colors" title="Copiază link">
                          <Share2 className="w-3.5 h-3.5" />
                        </button>
                        {isMod && user?.id !== author.id && (
                          <button onClick={() => setWarningPost({ author_id: p.author_id, author_username: author.username, ...p })} className="text-rose-500 hover:text-rose-400 transition-colors" title={t('topic.warn_member')}>
                            <AlertTriangle className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {(isMod || user?.id === author.id) && !isFirst && (
                          <button onClick={() => handleDeletePost(p.id)} className="text-zinc-600 hover:text-rose-500 transition-colors" title="Șterge postare">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Post Content Body */}
                    <div className="p-5 sm:p-6 flex-1 text-zinc-200 text-sm leading-relaxed font-sans" onClick={handleContentClick}>
                      {editingPostId === p.id ? (
                        <div className="space-y-3">
                          <ReplyEditor value={editPostContent} onChange={setEditPostContent} />
                          <div className="flex gap-2">
                            <button onClick={() => handleEditPost(p.id)} className="bg-[var(--tf-primary)] text-black px-4 py-2 font-chakra font-black text-xs uppercase">{t('common.save_changes')}</button>
                            <button onClick={() => setEditingPostId(null)} className="bg-[#06080d] border border-[#141824] text-zinc-400 px-4 py-2 font-chakra font-bold text-xs uppercase">{t('common.cancel')}</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div 
                            className="tf-markdown space-y-2" 
                            dangerouslySetInnerHTML={{ 
                              __html: renderMarkdown(
                                translations[`post_${p.id}`]?.showTranslated 
                                  ? translations[`post_${p.id}`].translatedText 
                                  : p.content
                              ) 
                            }}
                          />
                          {p.attachments && p.attachments.length > 0 && <AttachmentList items={p.attachments} />}
                        </>
                      )}

                      {p.signature && (
                        <div className="mt-6 pt-3 border-t border-[#141824] text-xs text-zinc-500 font-mono italic">
                          {p.signature}
                        </div>
                      )}
                    </div>

                    {/* Post Bottom Footer Actions */}
                    <footer className="px-5 py-3 bg-[#06080d] border-t border-[#141824] flex items-center justify-between flex-wrap gap-3">
                      {/* Reactions */}
                      <div className="relative group flex items-center gap-2">
                        <div className="flex items-center gap-1.5">
                          {REACTIONS.filter(r => (p.reactions?.[r.id] || 0) > 0).map(r => (
                            <span key={r.id} className="text-xs px-2.5 py-1 bg-[#090c12] border border-[#181e2b] text-zinc-300 flex items-center gap-1.5 font-mono shadow-sm">
                              {r.imgUrl ? <img src={r.imgUrl} alt={r.label} className="w-4 h-4 object-contain" /> : <span>{r.icon || r.emoji}</span>} 
                              <span className="font-bold text-white">{p.reactions?.[r.id]}</span>
                            </span>
                          ))}
                          
                          {user && (
                            <button className="px-3 py-1 bg-[#090c12] hover:bg-[#0e131d] border border-[var(--tf-primary)]/40 text-[var(--tf-primary)] hover:border-[var(--tf-primary)] text-xs font-chakra font-bold uppercase tracking-wider flex items-center gap-1.5 transition-colors cursor-pointer">
                              <span>🙂</span> 
                              <span>{t('topic.react', 'Reacționează')}</span>
                            </button>
                          )}
                        </div>

                        {/* Reaction popup on hover */}
                        {user && (
                          <div className="absolute bottom-full left-0 mb-2 hidden group-hover:flex bg-[#090c12] border border-[#181e2b] p-2 shadow-2xl gap-1.5 z-50 backdrop-blur-xl">
                            {REACTIONS.map((r) => (
                              <button 
                                key={r.id} 
                                onClick={() => handleReact(p.id, r.id)}
                                className="w-8 h-8 bg-[#06080d] hover:bg-[#0e131d] border border-[#141824] hover:border-[var(--tf-primary)] flex items-center justify-center text-base transition-transform hover:scale-125 cursor-pointer"
                                title={r.id}
                              >
                                {r.imgUrl ? <img src={r.imgUrl} alt={r.label} className="w-5 h-5 object-contain" /> : (r.icon || r.emoji)}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Post Action Buttons */}
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => handleTranslate(`post_${p.id}`, p.content)}
                          className="px-2.5 py-1 bg-[#090c12] hover:bg-[#0e131d] border border-[#141824] hover:border-[#181e2b] text-zinc-400 hover:text-white text-xs font-chakra font-bold uppercase transition-colors flex items-center gap-1.5"
                        >
                          <Languages className="w-3.5 h-3.5" />
                          <span>{translations[`post_${p.id}`]?.showTranslated ? t('topic.show_original', 'Original') : t('topic.translate_post', 'Tradu')}</span>
                        </button>
                        
                        {user && (user.id === author.id || isMod) && (
                          <button 
                            onClick={() => { setEditPostContent(p.content); setEditingPostId(p.id); }}
                            className="px-2.5 py-1 bg-[#090c12] hover:bg-[#0e131d] border border-[#141824] hover:border-[#181e2b] text-zinc-400 hover:text-white text-xs font-chakra font-bold uppercase transition-colors flex items-center gap-1.5"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                            <span>{t('common.edit', 'Editează')}</span>
                          </button>
                        )}
                        
                        <button 
                          onClick={() => { setReply(prev => prev + "\n> " + p.content.split("\n").join("\n> ") + "\n\n"); window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" }); }}
                          className="px-2.5 py-1 bg-[#090c12] hover:bg-[#0e131d] border border-[#141824] hover:border-[#181e2b] text-zinc-400 hover:text-white text-xs font-chakra font-bold uppercase transition-colors flex items-center gap-1.5"
                        >
                          <Quote className="w-3.5 h-3.5" />
                          <span>{t('common.quote', 'Citează')}</span>
                        </button>
                        
                        {user && user.id !== author.id && (
                          <button 
                            onClick={() => setReportingPost(p.id)}
                            className="px-2.5 py-1 bg-[#090c12] hover:bg-[#0e131d] border border-[#141824] hover:border-[#181e2b] text-zinc-500 hover:text-rose-400 text-xs font-chakra font-bold uppercase transition-colors flex items-center gap-1.5"
                          >
                            <Flag className="w-3.5 h-3.5" />
                            <span>{t('common.report', 'Raportează')}</span>
                          </button>
                        )}
                      </div>
                    </footer>
                  </section>
                </article>
              );
            })}
          </div>

          {/* 3. REPLY EDITOR BOX */}
          {!data.topic.locked && !data.topic.archived && user ? (
            <section className="bg-[#090c12] border border-[#151924] shadow-2xl flex flex-col md:flex-row overflow-hidden">
              <aside className="bg-[#06080d] border-b md:border-b-0 md:border-r border-[#141824] p-5 w-full md:w-[220px] shrink-0 flex flex-col items-center justify-start text-center space-y-3">
                <Username user={user} className="text-base font-chakra font-black tracking-wide" />
                <Avatar user={user} size={88} hover />
                <span className="text-[10px] font-mono text-zinc-500 uppercase">
                  Răspuns ca {user.username}
                </span>
              </aside>
              <div className="flex-1 min-w-0">
                <ReplyEditor
                  value={reply}
                  onChange={setReply}
                  onSubmit={handleReply}
                  attachments={replyAttachments}
                  onAttachmentsChange={setReplyAttachments}
                  placeholder={t('forums.write_reply', 'Scrie un răspuns...')}
                />
              </div>
            </section>
          ) : (
            <div className="bg-[#090c12] border border-[#151924] p-6 text-center text-zinc-400 font-mono text-xs shadow-xl">
              {data.topic.locked || data.topic.archived ? (
                <div className="space-y-2">
                  <Lock className="w-6 h-6 text-rose-500 mx-auto" />
                  <p className="font-bold text-white uppercase font-chakra">{t('common.locked', 'Acest topic este blocat')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="font-bold text-white uppercase font-chakra">{t('topic.login_to_reply', 'Trebuie să fii autentificat pentru a răspunde.')}</p>
                  <Link to="/login" className="inline-block px-4 py-2 bg-[var(--tf-primary)] text-black font-chakra font-black text-xs uppercase">
                    Autentificare
                  </Link>
                </div>
              )}
            </div>
          )}

        </div>

        {/* SIDEBAR COLUMN (3 or 4 Cols) */}
        <aside className="lg:col-span-3 space-y-5">
          
          {/* STATISTICI WIDGET */}
          <div className="bg-[#090c12] border border-[#151924] overflow-hidden shadow-2xl">
            <div className="px-4 py-3 bg-[#06080d] border-b border-[#141824] flex items-center gap-2">
              <Activity className="w-4 h-4 text-[var(--tf-primary)]" />
              <h3 className="font-chakra font-black text-xs uppercase tracking-wider text-white">
                {t('profile.stats', 'STATISTICI TOPIC')}
              </h3>
            </div>
            
            <div className="p-4 space-y-2.5 text-xs font-mono bg-[#06080d]/40">
              <div className="flex justify-between items-center text-zinc-400">
                <span className="flex items-center gap-1.5 text-zinc-500">
                  <User className="w-3.5 h-3.5" /> 
                  <span>Creat de</span>
                </span>
                <Link to={`/u/${data.posts[0]?.author_username}`} className="font-bold text-white hover:text-[var(--tf-primary)] transition-colors">
                  {data.posts[0]?.author_username || 'Admin'}
                </Link>
              </div>

              <div className="flex justify-between items-center text-zinc-400">
                <span className="flex items-center gap-1.5 text-zinc-500">
                  <MessageSquare className="w-3.5 h-3.5" /> 
                  <span>Răspunsuri</span>
                </span>
                <span className="font-bold text-[var(--tf-primary)]">{data.total ? data.total - 1 : 0}</span>
              </div>

              <div className="flex justify-between items-center text-zinc-400">
                <span className="flex items-center gap-1.5 text-zinc-500">
                  <Eye className="w-3.5 h-3.5" /> 
                  <span>Vizualizări</span>
                </span>
                <span className="font-bold text-cyan-400">{data.topic.views || 0}</span>
              </div>

              <div className="flex justify-between items-center text-zinc-400">
                <span className="flex items-center gap-1.5 text-zinc-500">
                  <Shield className="w-3.5 h-3.5" /> 
                  <span>Status</span>
                </span>
                <span className={`font-bold px-2 py-0.5 text-[10px] uppercase font-mono ${data.topic.locked ? 'bg-rose-500/10 border border-rose-500/30 text-rose-400' : 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400'}`}>
                  {data.topic.locked ? t('common.locked', 'BLOCAT') : t('common.active', 'ACTIV')}
                </span>
              </div>

              <div className="flex justify-between items-center text-zinc-400 pt-2 border-t border-[#141824]">
                <span className="flex items-center gap-1.5 text-zinc-500">
                  <Clock className="w-3.5 h-3.5" /> 
                  <span>Ultimul Răspuns</span>
                </span>
                <span className="text-[11px] text-zinc-300 font-bold">
                  {formatDistanceToNow(new Date(data.topic.last_post_at || data.topic.created_at))} {t('common.ago', 'în urmă')}
                </span>
              </div>
            </div>
          </div>

          {/* MEMBRI PARTICIPANȚI WIDGET */}
          <div className="bg-[#090c12] border border-[#151924] overflow-hidden shadow-2xl">
            <div className="px-4 py-3 bg-[#06080d] border-b border-[#141824] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-cyan-400" />
                <h3 className="font-chakra font-black text-xs uppercase tracking-wider text-white">
                  {t('common.members', 'MEMBRI')}
                </h3>
              </div>
              <span className="text-[10px] font-mono text-cyan-400 font-bold bg-cyan-500/10 px-2 py-0.5 border border-cyan-500/20">
                {Array.from(new Set(data.posts.map(p => p.author_username))).length}
              </span>
            </div>

            <div className="p-4 flex flex-wrap gap-2 bg-[#06080d]/40">
              {(() => {
                const map = new Map();
                data.posts.forEach(p => {
                  if (p.author && !map.has(p.author.id || p.author_username)) {
                    map.set(p.author.id || p.author_username, p.author);
                  }
                });
                return Array.from(map.values()).map((author, idx) => (
                  <Link key={author.id || idx} to={`/u/${author.username}`} title={author.username} className="hover:scale-110 transition-transform">
                    <Avatar user={author} size={36} hover />
                  </Link>
                ));
              })()}
            </div>
          </div>

          {/* REGULI TOPIC WIDGET */}
          <div className="bg-[#090c12] border border-[#151924] overflow-hidden shadow-2xl">
            <div className="px-4 py-3 bg-[#06080d] border-b border-[#141824] flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-amber-400" />
              <h3 className="font-chakra font-black text-xs uppercase tracking-wider text-amber-400">
                {t('topic.rules', 'REGULI TOPIC')}
              </h3>
            </div>
            
            <div className="p-4 space-y-2 text-xs font-mono bg-[#06080d]/40">
              <div className="flex items-start gap-2.5 p-2.5 bg-[#06080d] border border-[#141824] text-zinc-300">
                <ShieldCheck className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                <span>{t('topic.rule_respect', 'Respectă regulamentul comunității.')}</span>
              </div>
              <div className="flex items-start gap-2.5 p-2.5 bg-[#06080d] border border-[#141824] text-zinc-300">
                <Ban className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
                <span>{t('topic.rule_spam', 'Fără spam, flame sau reclame externe.')}</span>
              </div>
              <div className="flex items-start gap-2.5 p-2.5 bg-[#06080d] border border-[#141824] text-zinc-300">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                <span>{t('topic.rule_replies', 'Folosește răspunsuri clare și utile.')}</span>
              </div>
            </div>
          </div>

        </aside>

      </div>

      {/* Report Modal */}
      {reportingPost && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-[#090c12] border border-[#181e2b] p-6 w-full max-w-md shadow-2xl">
            <h2 className="text-sm font-chakra font-black uppercase text-rose-400 mb-4 flex items-center gap-2">
              <Flag className="w-4 h-4 text-rose-500" /> 
              <span>{t('common.report', 'Raportează Postare')}</span>
            </h2>
            <textarea
              className="w-full bg-[#06080d] border border-[#141824] p-3 text-xs text-white outline-none focus:border-[var(--tf-primary)] min-h-[100px] mb-4 font-sans"
              placeholder="Motivul raportului..."
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
            />
            <div className="flex justify-end gap-3">
              <button onClick={() => setReportingPost(null)} className="px-4 py-2 text-xs font-chakra uppercase font-bold text-zinc-400 hover:text-white">
                {t('common.cancel', 'Renunță')}
              </button>
              <button onClick={submitReport} className="bg-[var(--tf-primary)] text-black px-5 py-2 text-xs font-chakra uppercase font-black">
                {t('common.submit', 'Trimite')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Warn Modal */}
      {warningPost && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-[#090c12] border border-rose-500/30 p-6 w-full max-w-md shadow-2xl">
            <h2 className="text-sm font-chakra font-black uppercase text-rose-400 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-rose-500" /> 
              <span>{t('topic.warn_member', 'Avertisment Membru')}</span>
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-chakra font-bold text-zinc-500 uppercase tracking-widest mb-1">{t('topic.warning_reason', 'Motiv')}</label>
                <input
                  type="text"
                  className="w-full bg-[#06080d] border border-[#141824] p-2.5 text-xs text-white outline-none focus:border-[var(--tf-primary)]"
                  placeholder="Ex: Limbaj licențios"
                  value={warningReason}
                  onChange={(e) => setWarningReason(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-[10px] font-chakra font-bold text-zinc-500 uppercase tracking-widest mb-1">{t('topic.warning_points', 'Puncte Avertisment')}</label>
                <input
                  type="number"
                  min="1" max="10"
                  className="w-full bg-[#06080d] border border-[#141824] p-2.5 text-xs text-white outline-none focus:border-[var(--tf-primary)]"
                  value={warningPoints}
                  onChange={(e) => setWarningPoints(e.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setWarningPost(null)} className="px-4 py-2 text-xs font-chakra uppercase font-bold text-zinc-400 hover:text-white">
                {t('common.cancel', 'Renunță')}
              </button>
              <button onClick={submitWarning} className="bg-rose-600 hover:bg-rose-500 text-white px-5 py-2 text-xs font-chakra uppercase font-black">
                {t('topic.submit_warning', 'Trimite Avertisment')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox for inline images */}
      {lightbox && (
        <div className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center p-4 backdrop-blur-md" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="Fullscreen" className="max-w-full max-h-full object-contain cursor-zoom-out shadow-2xl border border-[#181e2b]" />
          <button className="absolute top-6 right-6 text-white/50 hover:text-rose-500 bg-[#090c12] border border-[#181e2b] w-10 h-10 flex items-center justify-center transition-all" onClick={(e) => { e.stopPropagation(); setLightbox(null); }}>
             <X className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
}
